/**
 * A plan refusal means this tab's copy of the plan is stale: re-read it, once,
 * for every surface.
 *
 * The plan is read once per session (`currentPlan.ts`, infinite `staleTime`).
 * When the API refuses a write with `PERM003` because the plan does not include
 * the feature — the plan changed mid-session, or this tab's copy was wrong —
 * the surface that made the call says so. Every OTHER surface kept offering the
 * control until a reload: the Invites tab still offered Resend, the org menu
 * still offered "New tenant", each one a click away from the same refusal.
 *
 * So the refusal re-reads the plan, and every gate (`useEntitlement`) follows.
 * It is done HERE, on the client, rather than at the call sites: the writes
 * that can meet a plan refusal live in several packages behind several
 * adapters (`@authe/teams`' store, the org-users source, the tenancy source),
 * some with a caller that inspects the error and some without, and all of them
 * ride the one `ApiClient`. A call site added tomorrow is covered without
 * knowing this exists.
 *
 * Which refusals count is decided by the recognisers the surfaces already use
 * to SAY the refusal, so "said as the plan's" and "re-reads the plan" cannot
 * disagree about what a plan refusal is.
 */
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@authe/ui-core/api";
import { isCustomRolesRefusal } from "@authe/teams";
import { isTenancyCreationRefusal } from "@/components/tenancyCreation";
import { CURRENT_SUBSCRIPTION_QUERY_KEY } from "./currentPlan";

/** Is `error` the API refusing something because the caller's plan lacks it? */
export function isPlanRefusal(error: unknown): boolean {
  return isCustomRolesRefusal(error) || isTenancyCreationRefusal(error);
}

/**
 * Re-read the plan whenever `client` resolves a plan refusal. Mounted once, by
 * `<DashboardPlan>`, beside the read it refreshes.
 */
export function useRefreshPlanOnRefusal(): void {
  const client = useApiClient();
  const queryClient = useQueryClient();
  React.useEffect(
    () =>
      client.onErrorEnvelope((envelope) => {
        if (!isPlanRefusal(envelope)) return;
        void queryClient.invalidateQueries({ queryKey: CURRENT_SUBSCRIPTION_QUERY_KEY });
      }),
    [client, queryClient],
  );
}
